import { useState, useEffect, useCallback } from 'react'
import { StickyNote, RefreshCw, Plus, Trash2, Pencil, X, Tag } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

export default function NotesPage() {
  const [notes, setNotes] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [tagInput, setTagInput] = useState('')
  const [tags, setTags] = useState<string[]>([])

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/content/notes')
      setNotes(data.notes || data || [])
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const addTag = () => {
    const t = tagInput.trim().toLowerCase()
    if (t && !tags.includes(t)) setTags(prev => [...prev, t])
    setTagInput('')
  }

  const removeTag = (tag: string) => setTags(prev => prev.filter(t => t !== tag))

  const createNote = async () => {
    if (!title.trim() && !content.trim()) return
    try {
      await api.post('/api/content/notes', { title: title.trim(), content: content.trim(), tags })
      setTitle('')
      setContent('')
      setTags([])
      setShowForm(false)
      fetchData()
    } catch { /* silently fail */ }
  }

  const deleteNote = async (id: string) => {
    try {
      await api.delete(`/api/content/notes/${id}`)
      setNotes(prev => prev.filter(n => n.id !== id))
    } catch { /* silently fail */ }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <StickyNote size={20} className="text-amber" /> Notes
          </h1>
          <p className="text-[10px] text-text-secondary">Personal scratchpad for ideas and observations</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setShowForm(!showForm)} className="btn-primary text-xs">
            <Plus size={12} /> New Note
          </button>
          <button type="button" onClick={fetchData} className="btn-secondary text-xs">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="card p-4 border-amber/30 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold text-text-primary">New Note</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-text-muted hover:text-text-primary">
              <X size={14} />
            </button>
          </div>
          <input value={title} onChange={e => setTitle(e.target.value)}
            placeholder="Title (optional)"
            className="input py-2 text-xs w-full" />
          <textarea value={content} onChange={e => setContent(e.target.value)}
            placeholder="Write your note..."
            rows={4}
            className="input py-2 text-xs w-full resize-y font-mono" />
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <Tag size={10} className="text-text-muted" />
              <input value={tagInput} onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag() } }}
                placeholder="Add tag..."
                className="input py-1 text-[10px] w-24" />
              <button type="button" onClick={addTag} className="text-[10px] text-blue hover:underline">add</button>
            </div>
            {tags.map(tag => (
              <span key={tag} className="inline-flex items-center gap-1 bg-amber/10 text-amber text-[10px] px-1.5 py-0.5 rounded">
                {tag}
                <button type="button" onClick={() => removeTag(tag)} className="hover:text-rose"><X size={8} /></button>
              </span>
            ))}
          </div>
          <button type="button" onClick={createNote} className="btn-primary text-xs">
            Save Note
          </button>
        </div>
      )}

      {loading && notes.length === 0 && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
        </div>
      )}

      {!loading && notes.length === 0 && !showForm && (
        <div className="card p-8 text-center">
          <StickyNote size={32} className="text-amber/40 mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-text-primary mb-1">No notes yet</h3>
          <p className="text-[11px] text-text-secondary max-w-sm mx-auto">
            Click "New Note" to create your first note. Use tags to organise.
          </p>
        </div>
      )}

      {/* Notes grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {notes.map(note => (
          <div key={note.id} className="card p-4 hover:border-border-light transition-colors flex flex-col">
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="text-sm font-semibold text-text-primary truncate flex-1">
                {note.title || 'Untitled'}
              </h3>
              <div className="flex items-center gap-1 shrink-0">
                <button type="button" onClick={() => deleteNote(note.id)}
                  className="text-text-muted hover:text-rose transition-colors" title="Delete note">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
            <p className="text-[11px] text-text-secondary leading-relaxed whitespace-pre-wrap flex-1 mb-2">
              {note.content}
            </p>
            {note.tags && note.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {note.tags.map((tag: string) => (
                  <span key={tag} className="bg-amber/10 text-amber text-[9px] px-1.5 py-0.5 rounded">{tag}</span>
                ))}
              </div>
            )}
            {note.created_at && (
              <span className="text-[9px] text-text-muted">{relativeTime(note.created_at)}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
